// src/components/OrderHistoryPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Divider,
  Chip,
  Stack,
  Avatar,
  CircularProgress,
  Snackbar,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  ExpandMore,
  ShoppingBag,
  CheckCircle,
  LocalShipping,
  HourglassEmpty,
  Cancel,
} from "@mui/icons-material";
import { fetchOrderHistory } from "../api/checkoutApi";
import { getUserProfile } from "../utils/auth";
import BackButton from "./BackButton";

const STATUS_CONFIG = {
  confirmed: { label: "Confirmed", color: "success", icon: <CheckCircle fontSize="small" /> },
  processing: { label: "Processing", color: "warning", icon: <HourglassEmpty fontSize="small" /> },
  shipped: { label: "Shipped", color: "info", icon: <LocalShipping fontSize="small" /> },
  delivered: { label: "Delivered", color: "success", icon: <CheckCircle fontSize="small" /> },
  cancelled: { label: "Cancelled", color: "error", icon: <Cancel fontSize="small" /> },
};

function statusConfig(status) {
  return STATUS_CONFIG[String(status).toLowerCase()] || { label: status || "Unknown", color: "default", icon: null };
}

function OrderCard({ order }) {
  const cfg = statusConfig(order.status);
  const orderDate = order.created_at || order.date_placed || order.order_date;
  const items = order.items || [];

  const formatPrice = (p) => {
    const n = typeof p === "string" ? parseFloat(p) : p;
    return isNaN(n) ? "0.00" : n.toFixed(2);
  };

  return (
    <Accordion elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: "8px !important", mb: 2, "&:before": { display: "none" } }}>
      <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5 }}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Order Number
            </Typography>
            <Typography variant="subtitle2" fontWeight="bold">
              {order.order_id || order.order_number || "—"}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary" display="block">
              Date
            </Typography>
            <Typography variant="body2">
              {orderDate
                ? new Date(orderDate).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })
                : "—"}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Typography variant="caption" color="text.secondary" display="block">
              Total
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              ${formatPrice(order.total)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Chip
              icon={cfg.icon}
              label={cfg.label}
              color={cfg.color}
              size="small"
              variant="outlined"
            />
          </Grid>
        </Grid>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 3, pb: 3 }}>
        <Divider sx={{ mb: 2 }} />

        {/* Items */}
        {items.length > 0 ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Items ({items.length})
            </Typography>
            <Stack spacing={1.5}>
              {items.map((item, idx) => (
                <Stack key={idx} direction="row" spacing={2} alignItems="center">
                  <Avatar
                    variant="rounded"
                    sx={{ width: 48, height: 48, bgcolor: "grey.200" }}
                    src={Array.isArray(item.image) ? item.image[0] : item.image}
                  >
                    <ShoppingBag fontSize="small" />
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight="medium">
                      {item.name || item.title || item.article_id || "Item"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.size ? `Size: ${item.size} · ` : ""}Qty: {item.quantity}
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="medium">
                    ${formatPrice(item.price * item.quantity)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No item details available.
          </Typography>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Price breakdown */}
        <Grid container spacing={0.5}>
          {[
            { label: "Subtotal", value: `$${formatPrice(order.subtotal)}` },
            {
              label: "Shipping",
              value: !order.shipping || order.shipping === 0 ? "FREE" : `$${formatPrice(order.shipping)}`,
            },
            { label: "Tax", value: `$${formatPrice(order.tax)}` },
            ...(order.discount > 0
              ? [{ label: "Discount", value: `-$${formatPrice(order.discount)}`, color: "success.main" }]
              : []),
            { label: "Total", value: `$${formatPrice(order.total)}`, bold: true },
          ].map(({ label, value, bold, color }) => (
            <React.Fragment key={label}>
              <Grid item xs={6}>
                <Typography variant="body2" fontWeight={bold ? "bold" : "normal"} color="text.secondary">
                  {label}
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: "right" }}>
                <Typography variant="body2" fontWeight={bold ? "bold" : "normal"} color={color || "text.primary"}>
                  {value}
                </Typography>
              </Grid>
            </React.Fragment>
          ))}
        </Grid>

        {/* Shipping address */}
        {order.shipping_address && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Shipped to
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {[
                `${order.shipping_address.first_name || ""} ${order.shipping_address.last_name || ""}`.trim(),
                order.shipping_address.street_address,
                order.shipping_address.city,
                order.shipping_address.state,
                order.shipping_address.zip_code,
                order.shipping_address.country,
              ]
                .filter(Boolean)
                .join(", ")}
            </Typography>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

export default function OrderHistoryPage() {
  const navigate = useNavigate();
  const userProfile = getUserProfile();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ open: false, message: "", severity: "error" });

  useEffect(() => {
    if (!userProfile?.customer_id) {
      setError("Please log in to view your order history.");
      setLoading(false);
      return;
    }

    fetchOrderHistory(userProfile.customer_id)
      .then((data) => {
        setOrders(data);
      })
      .catch((err) => {
        setError(err.message || "Failed to load order history.");
        setToast({ open: true, message: err.message || "Failed to load order history.", severity: "error" });
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <BackButton />

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Order History
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View and track all your past orders.
        </Typography>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Paper elevation={0} sx={{ p: 6, textAlign: "center", border: "1px solid #e5e7eb", borderRadius: 2 }}>
          <ShoppingBag sx={{ fontSize: 64, color: "grey.300", mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {error}
          </Typography>
          <Button variant="contained" onClick={() => navigate("/")} sx={{ mt: 2, bgcolor: "black", "&:hover": { bgcolor: "#111" } }}>
            Continue Shopping
          </Button>
        </Paper>
      )}

      {!loading && !error && orders.length === 0 && (
        <Paper elevation={0} sx={{ p: 6, textAlign: "center", border: "1px solid #e5e7eb", borderRadius: 2 }}>
          <ShoppingBag sx={{ fontSize: 64, color: "grey.300", mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No orders yet
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Your order history will appear here after you place an order.
          </Typography>
          <Button variant="contained" onClick={() => navigate("/")} sx={{ bgcolor: "black", "&:hover": { bgcolor: "#111" } }}>
            Start Shopping
          </Button>
        </Paper>
      )}

      {!loading && !error && orders.length > 0 && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {orders.length} order{orders.length !== 1 ? "s" : ""} found
          </Typography>
          {orders.map((order, idx) => (
            <OrderCard key={order.order_id || order.order_number || idx} order={order} />
          ))}
        </Box>
      )}
    </Container>
  );
}
